import classNames from 'classnames';
import { FieldInputProps } from 'formik';
import React, { useCallback } from 'react';
import UDFormFieldWrapper from 'ud-form/wrapper';
import { UDFormComponentProps } from '../types';
import UDInput, { UDInputProps } from './component';
import UDPasswordInput from './component/password';

type Props = UDFormComponentProps &
  Omit<UDInputProps, 'hasError' | 'valid' | 'ref'> & {
    inputComponent?: React.ComponentType<any> | null;
    ref?: React.ForwardedRef<HTMLInputElement>;
  };

const UDFormInput = (props: Props, ref: React.ForwardedRef<HTMLInputElement>) => {
  const { name, containerProps, label, required, labelProps, inputComponent, onChange, onBlur, errorVisible, ...otherInputProps } =
    props;

  const Component = inputComponent ? inputComponent : otherInputProps.type === 'password' ? UDPasswordInput : UDInput;

  const handleChange = useCallback(
    (field: FieldInputProps<any>) => (e: React.ChangeEvent<any>) => {
      field.onChange(e);

      if (onChange) {
        onChange(e);
      }
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (field: FieldInputProps<any>) => (e: React.FocusEvent<any>) => {
      field.onBlur(e);

      if (onBlur) {
        onBlur(e);
      }
    },
    [onBlur],
  );

  return (
    <UDFormFieldWrapper
      name={name}
      containerProps={containerProps}
      label={label}
      labelProps={labelProps}
      required={required}
      errorVisible={errorVisible}
    >
      {({ field, meta }) => (
        <Component
          ref={ref}
          {...otherInputProps}
          wrapperProps={{
            ...otherInputProps.wrapperProps,
            className: classNames('w-full', otherInputProps.wrapperProps?.className),
          }}
          {...field}
          onChange={handleChange(field)}
          onBlur={handleBlur(field)}
          hasError={(meta.touched || meta.value) && !!meta.error}
          valid={!meta.error && meta.touched && meta.value}
          className={classNames('w-full', otherInputProps?.className)}
        />
      )}
    </UDFormFieldWrapper>
  );
};

export default React.forwardRef(UDFormInput);
